import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRight, Sparkles, Leaf, Gauge, Users, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect, useRef } from 'react';
import { usePlatformStats } from '@/hooks/usePlatformStats';

function AnimatedCounter({ value, duration = 2 }: { value: number; duration?: number }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const controls = animate(count, value, { duration });
    return controls.stop;
  }, [value]);

  useEffect(() => {
    const unsubscribe = rounded.on('change', (v) => {
      if (ref.current) ref.current.textContent = String(v);
    });
    return unsubscribe;
  }, [rounded]);

  return <span ref={ref}>0</span>;
}

interface HeroSectionProps {
  onRequestAccess?: () => void;
}

export function HeroSection({ onRequestAccess }: HeroSectionProps) {
  const { data: stats } = usePlatformStats();
  const totalEntries = (stats?.tasks ?? 0) + (stats?.work_logs ?? 0) + (stats?.applications ?? 0) + (stats?.diary_entries ?? 0);
  return (
    <section className="relative overflow-hidden px-4 pb-32 pt-40 sm:px-6 lg:px-8">
      {/* Animated mesh background */}
      <div className="pointer-events-none absolute inset-0 mesh-gradient" />

      {/* Animated gradient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          animate={{ y: [-30, 30, -30], x: [-15, 15, -15], scale: [1, 1.1, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -left-32 top-10 h-96 w-96 rounded-full bg-primary/20 blur-[100px]"
        />
        <motion.div
          animate={{ y: [20, -20, 20], x: [15, -15, 15], scale: [1.1, 1, 1.1] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-32 top-32 h-[28rem] w-[28rem] rounded-full bg-primary-glow/15 blur-[100px]"
        />
        <motion.div
          animate={{ y: [15, -25, 15] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -bottom-20 left-1/4 h-80 w-80 rounded-full bg-success/12 blur-[100px]"
        />
        {/* Floating grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(to_right,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="relative mx-auto max-w-5xl text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-10 inline-flex items-center gap-2 rounded-full glass border border-primary/20 px-5 py-2.5 text-sm font-medium text-primary"
        >
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Sparkles className="h-4 w-4" />
          </motion.div>
          Now in Public Beta
          <ArrowRight className="h-3 w-3" />
        </motion.div>

        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-5xl font-extrabold tracking-tight text-foreground sm:text-6xl lg:text-7xl xl:text-8xl"
        >
          Your All-in-One{' '}
          <br className="hidden sm:block" />
          <span className="relative">
            <span className="text-gradient">Productivity Hub</span>
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 1, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -bottom-2 left-0 h-1 rounded-full gradient-primary opacity-60"
            />
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mt-8 max-w-2xl text-lg sm:text-xl text-muted-foreground leading-relaxed"
        >
          Manage tasks, track job applications, log work hours, and plan your
          upskilling journey — all in one beautifully crafted platform.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <Button asChild size="lg" className="group gap-2 px-8 text-base font-semibold rounded-2xl h-13 gradient-primary border-0 btn-glow transition-all duration-300 hover:scale-[1.03] shadow-lg shadow-primary/25">
            <Link to="/signup">
              Get Started Free
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="px-8 text-base rounded-2xl h-13 glass border-border/50 hover:bg-accent/50 transition-all duration-300 hover:scale-[1.02]">
            <Link to="/login">Sign In</Link>
          </Button>
        </motion.div>

        {onRequestAccess && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="mt-5 text-sm text-muted-foreground"
          >
            Not invited yet?{' '}
            <button
              onClick={onRequestAccess}
              className="text-primary font-medium hover:underline underline-offset-4 transition-colors"
            >
              Request early access →
            </button>
          </motion.p>
        )}

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-20 grid grid-cols-3 gap-6 sm:gap-8 max-w-lg mx-auto"
        >
          {[
            { value: stats?.modules ?? 7, suffix: '+', label: 'Modules', icon: Gauge },
            { value: stats?.users ?? 0, suffix: '+', label: 'Users', icon: Users },
            { value: totalEntries, suffix: '+', label: 'Entries Created', icon: FileText },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              whileHover={{ y: -4 }}
              className="text-center"
            >
              <p className="text-3xl sm:text-4xl font-extrabold font-display text-foreground">
                <AnimatedCounter value={stat.value} />{stat.suffix}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
                <stat.icon className="h-3 w-3 text-primary" />
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-16 flex items-center justify-center gap-6 text-xs text-muted-foreground"
        >
          <span className="flex items-center gap-1.5">
            <Leaf className="h-3.5 w-3.5 text-primary" /> Lightning fast
          </span>
          <span className="h-3 w-px bg-border" />
          <span>Free to start</span>
          <span className="h-3 w-px bg-border" />
          <span>No credit card</span>
        </motion.div>
      </div>
    </section>
  );
}
